/**
 * Horizontal pill of circular icon buttons — one per PlanConnectionRelationshipType, plus a
 * trailing red delete button — shown by PlanPage.tsx when a Plan-board connector is clicked,
 * letting the user assign or change what the connection means (see
 * PlanBoardConnection.relationshipType, types.ts) or remove it entirely. Purely presentational,
 * in the same "callers own what each item does" spirit as CircleMenu
 * (components/ui/CircleMenu.tsx), but always fully shown rather than extruding from a trigger —
 * PlanPage owns positioning it (a fixed-position portal anchored above the click point) and
 * clearing the selection on outside click/Escape/delete.
 *
 * Each button's label is shown as a custom black-rectangle tooltip (a pure-CSS
 * `::before`/`::after` pair on `.plan-connection-type-popover__item`, see app.css) rather than
 * the native `title` attribute, so it appears instantly on hover with a downward-pointing
 * triangle anchored to the button beneath it, instead of the browser's delayed default tooltip.
 */
import React from 'react';
import { PlanConnectionRelationshipType } from '../../types';
import { PLAN_CONNECTION_RELATIONSHIP_TYPES, PLAN_CONNECTION_RELATIONSHIP_META } from '../../utils/planConnectionRelationships';

interface PlanConnectionRelationshipPopoverProps {
    /** The connection's current relationshipType, or null if it hasn't been assigned one yet. */
    selectedType: PlanConnectionRelationshipType | null;
    onSelect: (type: PlanConnectionRelationshipType) => void;
    /** Removes the connection entirely. Rendered as a red X, rightmost in the pill. */
    onDelete: () => void;
}

export default function PlanConnectionRelationshipPopover({ selectedType, onSelect, onDelete }: PlanConnectionRelationshipPopoverProps) {
    return (
        <div className='plan-connection-type-popover'>
            {PLAN_CONNECTION_RELATIONSHIP_TYPES.map((type) => {
                const meta = PLAN_CONNECTION_RELATIONSHIP_META[type];
                return (
                    <span key={type} className='plan-connection-type-popover__item' data-tooltip-label={meta.label}>
                        <button
                            type='button'
                            className='plan-connection-type-popover__btn'
                            style={{ '--connection-type-color': meta.color } as React.CSSProperties}
                            aria-label={meta.label}
                            aria-pressed={selectedType === type}
                            onClick={() => onSelect(type)}
                        >
                            {meta.icon}
                        </button>
                    </span>
                );
            })}
            <span className='plan-connection-type-popover__item' data-tooltip-label='Delete relationship'>
                <button
                    type='button'
                    className='plan-connection-type-popover__delete-btn'
                    aria-label='Delete relationship'
                    onClick={onDelete}
                >
                    ✕
                </button>
            </span>
        </div>
    );
}
